/**
 * Hard-coded Board Rules Utilities
 * Contains the core business logic for status assignment that cannot be changed by users
 * 
 * These rules take precedence over user configuration and provide consistent
 * business logic across all board implementations.
 */

// Hard-coded business rules that cannot be changed by users
const HARD_CODED_RULES = [
    {
        id: 1,
        name: 'NEW_KEYWORDS',
        description: 'Items with "new" in status name go to New column',
        keywords: ['new'],
        targetColumn: 'new',
        priority: 1
  },
    {
        id: 2,
        name: 'SCHEDULED_OR_DISPATCH_KEYWORDS',
        description: 'Items with "scheduled" or "dispatch" in status name go to Scheduled/Dispatch column',
    keywords: ['scheduled', 'dispatch'],
      targetColumn: 'scheduled_dispatched',
      priority: 2
  },
    {
        id: 3,
        name: 'COMPLETED_KEYWORDS',
        description: 'Items with "completed" or "closed" in status name go to Completed column',
        keywords: ['completed', 'closed'],
        targetColumn: 'completed',
        priority: 3
  },
    {
        id: 4,
        name: 'WAITING_OR_HOLD_KEYWORDS',
        description: 'Items starting with "waiting" or containing "hold" or "pending" go to Waiting/Hold column',
        keywords: ['waiting', 'hold', 'pending'],
        targetColumn: 'waiting_on_hold',
        priority: 4
    },
    {
        id: 5,
        name: 'INVOICE_EXCLUSION_RULE',
        description: 'Items containing "invoice" are excluded from the board (handled by invoice boards)',
        keywords: ['invoice'],
        targetColumn: null, // null means exclude from board
        priority: 5,
        isExclusionRule: true
    },
    {
        id: 6,
        name: 'READY_TO_GO_KEYWORDS',
        description: 'Items with "ready" or "approved for scheduling" go to Scheduled/Dispatch column',
        keywords: ['ready to go', 'approved for scheduling'],
        targetColumn: 'scheduled_dispatched',
        priority: 6
    },
    {
        id: 7,
        name: 'VOID_CANCELLED_KEYWORDS',
        description: 'Items with "void", "cancelled", "withdrawn" go to Completed column',
        keywords: ['void', 'cancelled', 'withdrawn'],
        targetColumn: 'completed',
        priority: 7
    },
    {
        id: 8,
        name: 'INSPECTION_KEYWORDS',
        description: 'Items with "inspection" go to In Progress column',
        keywords: ['inspection'],
        targetColumn: 'in_progress',
        priority: 8
  }
];

/**
 * Apply hard-coded rules to determine the column for a project status
 * These rules take priority over user-configured settings
 * 
 * @param projectStatusId - The ID of the project status
 * @param statusName - The name/text of the status (case-insensitive matching)
 * @returns The column ID if a rule matches, null if excluded from board, undefined if no rule applies
 */
export function applyHardCodedRules(projectStatusId: number, statusName: string): string | null | undefined {
    if (!statusName) {
        return undefined;
  }

    const lowerStatusName = statusName.toLowerCase();

    // Apply rules in priority order
    for (const rule of HARD_CODED_RULES) {
        for (const keyword of rule.keywords) {
            const lowerKeyword = keyword.toLowerCase();

        // Special handling for "starts with" patterns
        if (rule.name === 'INVOICE_EXCLUSION_RULE') {
            if (lowerStatusName.includes(lowerKeyword)) {
                return null; // Exclude from board
            }
          continue;
        }

        // Special handling for WAITING_OR_HOLD_KEYWORDS rule
        if (rule.name === 'WAITING_OR_HOLD_KEYWORDS') {
            if (lowerKeyword === 'waiting') {
                // For "waiting" keyword, check if status starts with "waiting"
                if (lowerStatusName.startsWith(lowerKeyword)) {
                    return rule.targetColumn;
                }
            } else {
                // For "hold" and "pending" keywords, check if status contains the keyword
                if (lowerStatusName.includes(lowerKeyword)) {
                    return rule.targetColumn;
                }
            }
            continue; // Move to next keyword
        }

        // Check if status name contains the keyword (for all other cases)
        if (lowerStatusName.includes(lowerKeyword)) {
            // If it's an exclusion rule, return null to exclude from board
            if (rule.isExclusionRule) {
                return null;
            }
          // Otherwise return the target column
          return rule.targetColumn;
      }
    }
  }

    // No hard-coded rule applies
    return undefined;
}

/**
 * Get descriptions of all hard-coded rules for documentation/UI display
 * @returns Array of rule descriptions
 */
export function getHardCodedRuleDescriptions(): string[] {
    return HARD_CODED_RULES.map(rule => rule.description);
}

/**
 * Apply hard-coded rules to a list of project statuses for board configuration
 * This helper function makes it easy to process multiple statuses at once
 * 
 * @param statuses - Array of status objects with id and name/status properties
 * @returns Array of status assignments with hard-coded rule results
 */
export function applyHardCodedRulesToStatusList(statuses: Array<{ id: number; status?: string; name?: string; }>): Array<{
    statusId: number;
    statusName: string;
    ruleApplied: string | null;
    columnAssignment: string | null | undefined;
    isExcluded: boolean;
}> {
    return statuses.map(status => {
        const statusName = status.status || status.name || '';
        const columnAssignment = applyHardCodedRules(status.id, statusName);

        return {
            statusId: status.id,
            statusName,
            ruleApplied: columnAssignment === undefined ? null : (columnAssignment === null ? 'EXCLUDED' : columnAssignment),
            columnAssignment,
            isExcluded: columnAssignment === null
        };
    });
}

/**
 * Build status ID arrays for board columns based on hard-coded rules
 * This function takes a list of all available statuses and categorizes them by column
 * 
 * @param statuses - Array of all available project statuses from API
 * @returns Object with column IDs as keys and arrays of status IDs as values
 */
export function buildColumnStatusMaps(statuses: Array<{ id: number; status?: string; name?: string; }>): { [columnId: string]: number[] } {
    const statusAssignments = applyHardCodedRulesToStatusList(statuses);
    
    const columnMaps: { [columnId: string]: number[] } = {
        new: [],
        scheduled_dispatched: [],
        in_progress: [],
        waiting_on_hold: [],
        completed: []
    };
    
    // Group status IDs by their assigned columns
    statusAssignments.forEach(assignment => {
        if (assignment.columnAssignment && !assignment.isExcluded) {
            const columnId = assignment.columnAssignment;
            if (columnMaps[columnId]) {
                columnMaps[columnId].push(assignment.statusId);
            }
        }
    });
    
    return columnMaps;
}

/**
 * Check if a status should be excluded from the board based on hard-coded rules
 * @param statusName - The status name to check
 * @returns true if the status should be excluded from the board
 */
export function isStatusExcludedFromBoard(statusName: string): boolean {
    return applyHardCodedRules(0, statusName) === null;
}

/**
 * Get all hard-coded rules for external inspection/debugging
 * @returns Copy of the hard-coded rules array
 */
export function getAllHardCodedRules() {
    return [...HARD_CODED_RULES];
}

// Export for external access if needed
export { HARD_CODED_RULES };
